import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import Button from 'Components/Atoms/Button/Button';
import OutlineClose from 'Components/Atoms/OutlineIcons/OutlineClose';
import Icon from 'Components/Atoms/Icon/Icon';
import { markInvoiceAsNotDuplicate } from 'Services/invoices';

export default function InvoiceDuplicateWarning({ invoice, onDelete, onViewOriginal, onError }) {
    const { t } = useTranslation('invoices');
    const queryClient = useQueryClient();
    const [noDuplicateProcessing, setNoDuplicateProcessing] = useState(false);

    const handleNoDuplicate = async () => {
        setNoDuplicateProcessing(true);
        try {
            await markInvoiceAsNotDuplicate(invoice.id);
            await queryClient.invalidateQueries({ queryKey: ['invoices'] });
        } catch (error) {
            onError?.(error);
        } finally {
            setNoDuplicateProcessing(false);
        }
    };

    if (!invoice?.invoice_duplicated_id) return null;

    return (
        <div className="px-3 py-4 bg-warning/10 rounded-md mb-4">
            <div className="flex gap-3 items-start">
                <div className="shrink-0 flex py-0.5 text-[#985201]">
                    <Icon name="circle-info" size={24} color="currentColor" />
                </div>
                <div className="grow text-dark-blue">
                    <div className="font-semibold text-sm">{t('duplicate-invoice-warning')}</div>
                    <div className="opacity-50 mb-2 text-sm">{t('duplicate-invoice-message')}</div>
                    <div className="flex flex-wrap gap-x-2 gap-y-1">
                        <Button
                            size="extraSmall"
                            className="border-0 px-3"
                            onClick={onDelete}
                            disabled={!invoice?.is_deletable}
                        >
                            <Icon name="trash" size={18} color="currentColor" />
                            {t('delete')}
                        </Button>
                        <Button size="extraSmall" className="border-0 px-3" onClick={onViewOriginal}>
                            <Icon name="eye" size={18} color="currentColor" />
                            {t('view-original-invoice')}
                        </Button>
                        <Button
                            size="extraSmall"
                            className="border-0 px-3"
                            onClick={handleNoDuplicate}
                            isBusy={noDuplicateProcessing}
                        >
                            <OutlineClose />
                            {t('no-duplicate-invoice')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

InvoiceDuplicateWarning.propTypes = {
    invoice: PropTypes.object.isRequired,
    onDelete: PropTypes.func.isRequired,
    onViewOriginal: PropTypes.func.isRequired,
    onError: PropTypes.func,
};
